import React, { useState } from 'react';
import { 
  TrendingUp, 
  ChevronDown, 
  ChevronRight, 
  ChevronUp, 
  Plus, 
  Edit3, 
  Trash2, 
  Link, 
  FileText, 
  X,
  Clock,
  AlertCircle,
  TriangleAlert,
  CheckCircle,
  Flag,
  User,
  Calendar,
  Settings
} from 'lucide-react';

const SuccessPlanTable = () => {
  // Sample data structure with enhanced field definitions
  const [taskGroups, setTaskGroups] = useState([
    {
      id: 1,
      title: 'Project Setup',
      additional_fields: [
        { name: 'Budget', type: 'text' },
        { name: 'Department', type: 'single_choice', options: ['Engineering', 'Operations', 'Marketing', 'Sales'] },
        { name: 'Due Date', type: 'date' },
        { name: 'Skills Required', type: 'multiple_choice', options: ['React', 'Node.js', 'Python', 'Design', 'Testing'] }
      ],
      tasks: [
        {
          id: 1,
          title: 'Initial Planning',
          status: 'done',
          priority: 'high',
          assignee: 1,
          dueDate: '2024-01-15',
          createdDate: '2024-01-01',
          description: 'Complete initial project planning phase',
          waitDays: 0,
          type: 'Task',
          sharedInPortal: true,
          emailTemplate: 'Default Template',
          dependencies: [],
          documentation: 'This task involves setting up the initial project structure and defining key milestones.',
          additional_fields: {
            'Budget': '$5000',
            'Department': 'Engineering',
            'Due Date': '2024-01-20',
            'Skills Required': ['React', 'Design']
          }
        },
        {
          id: 2,
          title: 'Resource Allocation',
          status: 'in-progress',
          priority: 'medium',
          assignee: 2,
          dueDate: '2024-01-20',
          createdDate: '2024-01-05',
          description: 'Allocate necessary resources',
          waitDays: 2,
          type: 'Milestone',
          sharedInPortal: false,
          emailTemplate: 'Resource Template',
          dependencies: [1],
          documentation: 'Resource allocation includes team members, budget, and equipment needed for the project.',
          additional_fields: {
            'Budget': '$3000',
            'Department': 'Operations',
            'Due Date': '2024-01-25',
            'Skills Required': ['Node.js', 'Testing']
          }
        }
      ]
    },
    {
      id: 2,
      title: 'Development Phase',
      additional_fields: [
        { name: 'Technology', type: 'single_choice', options: ['React', 'Vue', 'Angular', 'Svelte'] },
        { name: 'Sprint', type: 'text' },
        { name: 'Launch Date', type: 'date' }
      ],
      tasks: [
        {
          id: 3,
          title: 'Core Development',
          status: 'pending',
          priority: 'high',
          assignee: 1,
          dueDate: '2024-02-01',
          createdDate: '2024-01-10',
          description: 'Develop core functionality',
          waitDays: 5,
          type: 'Task',
          sharedInPortal: true,
          emailTemplate: 'Dev Template',
          dependencies: [1, 2],
          documentation: 'Core development includes implementing the main features and functionality.',
          additional_fields: {
            'Technology': 'React',
            'Sprint': 'Sprint 1',
            'Launch Date': '2024-02-15'
          }
        }
      ]
    }
  ]);

  const [allusersnames] = useState([
    { id: 1, name: 'Alice Johnson' },
    { id: 2, name: 'Bob Smith' },
    { id: 3, name: 'Carol Davis' }
  ]);

  const [expandedGroups, setExpandedGroups] = useState(new Set([1, 2]));
  const [expandedDocs, setExpandedDocs] = useState(new Set());
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingStep, setEditingStep] = useState(null);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [showsavetemplate, setshowsavetemplate] = useState(false);
  const [managingFieldsForGroup, setManagingFieldsForGroup] = useState(null);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');
  const [newFieldOptions, setNewFieldOptions] = useState('');

  const statusOptions = ['pending', 'in-progress', 'done', 'overdue'];
  const typeOptions = ['Task', 'Milestone', 'Review', 'Approval'];
  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'date', label: 'Date' },
    { value: 'single_choice', label: 'Single Choice' },
    { value: 'multiple_choice', label: 'Multiple Choice' }
  ];

  // Helper functions
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const toggleDocumentation = (taskId) => {
    setExpandedDocs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const isTaskLocked = (task, groupId) => {
    if (!task.dependencies || task.dependencies.length === 0) return false;
    
    const allTasks = taskGroups.flatMap(group => group.tasks);
    
    return task.dependencies.some(depId => {
      const depTask = allTasks.find(t => t.id === depId);
      return !depTask || depTask.status !== 'done';
    });
  };

  const moveGroup = (groupId, direction) => {
    setTaskGroups(prev => {
      const groups = [...prev];
      const currentIndex = groups.findIndex(g => g.id === groupId);
      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      
      if (newIndex >= 0 && newIndex < groups.length) {
        [groups[currentIndex], groups[newIndex]] = [groups[newIndex], groups[currentIndex]];
      }
      
      return groups;
    });
  };

  const addStep = (groupId) => {
    const group = taskGroups.find(g => g.id === groupId);
    const groupAdditionalFields = group?.additional_fields || [];
    
    const newTask = {
      id: Date.now(),
      title: 'New Task',
      status: 'pending',
      priority: 'medium',
      assignee: 1,
      dueDate: new Date().toISOString().split('T')[0],
      createdDate: new Date().toISOString().split('T')[0],
      description: '',
      waitDays: 0,
      type: 'Task',
      sharedInPortal: false,
      emailTemplate: 'Default Template',
      dependencies: [],
      documentation: '',
      additional_fields: groupAdditionalFields.reduce((acc, field) => {
        acc[field.name] = field.type === 'multiple_choice' ? [] : '';
        return acc;
      }, {})
    };

    setTaskGroups(prev => prev.map(group => 
      group.id === groupId 
        ? { ...group, tasks: [...group.tasks, newTask] }
        : group
    ));
  };

  const updateStep = (groupId, taskId, updates) => {
    setTaskGroups(prev => prev.map(group => 
      group.id === groupId 
        ? {
            ...group,
            tasks: group.tasks.map(task => 
              task.id === taskId ? { ...task, ...updates } : task
            )
          }
        : group
    ));
  };

  const deleteStep = (groupId, taskId) => {
    setTaskGroups(prev => prev.map(group => 
      group.id === groupId 
        ? { ...group, tasks: group.tasks.filter(task => task.id !== taskId) }
        : group
    ));
  };

  const addFieldToGroup = (groupId, fieldName, fieldType, fieldOptions) => {
    if (!fieldName.trim()) return;
    
    const newField = {
      name: fieldName.trim(),
      type: fieldType,
      options: (fieldType === 'single_choice' || fieldType === 'multiple_choice') 
        ? fieldOptions.split(',').map(opt => opt.trim()).filter(opt => opt) 
        : []
    };
    
    setTaskGroups(prev => prev.map(group => {
      if (group.id === groupId) {
        const updatedAdditionalFields = [...(group.additional_fields || []), newField];
        const updatedTasks = group.tasks.map(task => ({
          ...task,
          additional_fields: {
            ...task.additional_fields,
            [fieldName.trim()]: fieldType === 'multiple_choice' ? [] : ''
          }
        }));
        
        return {
          ...group,
          additional_fields: updatedAdditionalFields,
          tasks: updatedTasks
        };
      }
      return group;
    }));
    
    setNewFieldName('');
    setNewFieldType('text');
    setNewFieldOptions('');
  };

  const removeFieldFromGroup = (groupId, fieldName) => {
    setTaskGroups(prev => prev.map(group => {
      if (group.id === groupId) {
        const updatedAdditionalFields = group.additional_fields.filter(field => field.name !== fieldName);
        const updatedTasks = group.tasks.map(task => {
          const updatedTaskFields = { ...task.additional_fields };
          delete updatedTaskFields[fieldName];
          return {
            ...task,
            additional_fields: updatedTaskFields
          };
        });
        
        return {
          ...group,
          additional_fields: updatedAdditionalFields,
          tasks: updatedTasks
        };
      }
      return group;
    }));
  };

  const updateTaskAdditionalField = (groupId, taskId, fieldName, value) => {
    setTaskGroups(prev => prev.map(group => 
      group.id === groupId 
        ? {
            ...group,
            tasks: group.tasks.map(task => 
              task.id === taskId ? { 
                ...task, 
                additional_fields: {
                  ...task.additional_fields,
                  [fieldName]: value
                }
              } : task
            )
          }
        : group
    ));
  };

const renderFieldInput = (group, task, field) => {
  const currentValue = task.additional_fields?.[field.name] || (field.type === 'multiple_choice' ? [] : '');

  switch (field.type) {
    case 'text':
      return (
        <input
          type="text"
          value={currentValue}
          onChange={(e) => updateTaskAdditionalField(group.id, task.id, field.name, e.target.value)}
          style={{
            width: '100%',
            maxWidth: '120px',
            padding: '4px 8px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '12px',
            backgroundColor: '#f9fafb'
          }}
          placeholder={`${field.name}...`}
        />
      );

    case 'date':
      return (
        <input
          type="date"
          value={currentValue}
          onChange={(e) => updateTaskAdditionalField(group.id, task.id, field.name, e.target.value)}
          style={{
            width: '100%',
            maxWidth: '120px',
            padding: '4px 8px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '12px',
            backgroundColor: '#f9fafb'
          }}
        />
      );

    case 'single_choice':
      return (
        <div style={{ position: 'relative', maxWidth: '120px' }}>
          <select
            value={currentValue}
            onChange={(e) => updateTaskAdditionalField(group.id, task.id, field.name, e.target.value)}
            style={{
              width: '100%',
              padding: '6px 28px 6px 10px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '12px',
              backgroundColor: '#ffffff',
              cursor: 'pointer',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%234b5563' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 8px center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '14px',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)',
              fontWeight: '500',
              color: currentValue ? '#1f2937' : '#9ca3af'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.08), 0 1px 2px 0 rgba(0, 0, 0, 0.03)';
              e.target.style.outline = 'none';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.03)';
            }}
            onMouseEnter={(e) => {
              if (e.target !== document.activeElement) {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.08)';
              }
            }}
            onMouseLeave={(e) => {
              if (e.target !== document.activeElement) {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.03)';
              }
            }}
          >
            <option value="" style={{ color: '#9ca3af', fontWeight: '400' }}>Select...</option>
            {field.options?.map(option => (
              <option key={option} value={option} style={{ 
                color: '#111827', 
                fontWeight: '500',
                padding: '8px 12px'
              }}>
                {option}
              </option>
            ))}
          </select>
        </div>
      );

    case 'multiple_choice':
      return (
        <div style={{ maxWidth: '120px', position: 'relative' }}>
          {/* Main Trigger Button */}
          <button
            type="button"
            aria-haspopup="listbox"
            aria-expanded={false}
            style={{
              width: '100%',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
              minHeight: '32px',
              padding: '6px 24px 6px 8px',
              cursor: 'pointer',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
              transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              fontSize: '11px',
              fontWeight: '500',
              color: '#374151',
              textAlign: 'left',
              outline: 'none'
            }}
            onClick={(e) => {
              const dropdown = e.currentTarget.nextElementSibling;
              const isVisible = dropdown.style.display === 'block';
              const isExpanded = !isVisible;
              
              // Close all other dropdowns
              document.querySelectorAll('.compact-multiselect-dropdown').forEach(d => {
                d.style.display = 'none';
              });
              
              dropdown.style.display = isExpanded ? 'block' : 'none';
              e.currentTarget.setAttribute('aria-expanded', isExpanded);
              
              // Update styling
              if (isExpanded) {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.08), 0 1px 3px 0 rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.borderBottomLeftRadius = '6px';
                e.currentTarget.style.borderBottomRightRadius = '6px';
              } else {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.05)';
              }
            }}
            onMouseEnter={(e) => {
              if (e.currentTarget.getAttribute('aria-expanded') !== 'true') {
                e.currentTarget.style.borderColor = '#cbd5e0';
                e.currentTarget.style.boxShadow = '0 1px 4px 0 rgba(0, 0, 0, 0.08)';
              }
            }}
            onMouseLeave={(e) => {
              if (e.currentTarget.getAttribute('aria-expanded') !== 'true') {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.05)';
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.currentTarget.click();
              } else if (e.key === 'Escape') {
                const dropdown = e.currentTarget.nextElementSibling;
                dropdown.style.display = 'none';
                e.currentTarget.setAttribute('aria-expanded', 'false');
              }
            }}
          >
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '2px',
              overflow: 'hidden',
              flex: 1,
              minHeight: '16px'
            }}>
              {Array.isArray(currentValue) && currentValue.length > 0 ? (
                currentValue.map((value, index) => (
                  <span
                    key={value}
                    style={{
                      backgroundColor: '#eff6ff',
                      color: '#2563eb',
                      padding: '1px 4px',
                      borderRadius: '3px',
                      fontSize: '8px',
                      fontWeight: '600',
                      border: '1px solid #bfdbfe',
                      whiteSpace: 'nowrap',
                      maxWidth: '50px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'inline-block'
                    }}
                    title={value}
                  >
                    {value}
                  </span>
                ))
              ) : (
                <span style={{ 
                  color: '#9ca3af', 
                  fontSize: '10px',
                  fontWeight: '400'
                }}>
                  Select...
                </span>
              )}
            </div>
            
            {/* Chevron Icon */}
            <svg
              style={{
                position: 'absolute',
                right: '6px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                color: '#6b7280',
                transition: 'transform 0.15s ease'
              }}
              width="12" 
              height="12" 
              viewBox="0 0 20 20" 
              fill="none"
            >
              <path 
                stroke="currentColor" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth="1.5" 
                d="M6 8l4 4 4-4"
              />
            </svg>
          </button>
          
          {/* Dropdown Menu */}
          <div
            className="compact-multiselect-dropdown"
            role="listbox"
            aria-multiselectable="true"
            style={{
              display: 'none',
              position: 'absolute',
              top: '100%',
              left: '0',
              right: '0',
              backgroundColor: '#ffffff',
              border: '1px solid #d1d5db',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              zIndex: 50000,
              maxHeight: '180px',
              overflowY: 'auto',
              marginTop: '-1px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with Select All/Clear All */}
            <div style={{
              padding: '8px',
              borderBottom: '1px solid #f1f5f9',
              backgroundColor: '#f8fafc',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  updateTaskAdditionalField(group.id, task.id, field.name, [...field.options]);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3b82f6',
                  fontSize: '9px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  padding: '2px 4px',
                  borderRadius: '3px',
                  transition: 'background-color 0.15s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#eff6ff'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                Select All
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  updateTaskAdditionalField(group.id, task.id, field.name, []);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#ef4444',
                  fontSize: '9px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  padding: '2px 4px',
                  borderRadius: '3px',
                  transition: 'background-color 0.15s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#fef2f2'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                Clear All
              </button>
            </div>

            {/* Options List */}
            <div style={{ maxHeight: '140px', overflowY: 'auto' }}>
              {field.options?.map((option, index) => {
                const isSelected = Array.isArray(currentValue) && currentValue.includes(option);
                return (
                  <label
                    key={option}
                    role="option"
                    aria-selected={isSelected}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 10px',
                      fontSize: '10px',
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#f0f9ff' : 'transparent',
                      color: isSelected ? '#1e40af' : '#374151',
                      transition: 'all 0.1s cubic-bezier(0.4, 0, 0.2, 1)',
                      borderBottom: index < field.options.length - 1 ? '1px solid #f8fafc' : 'none',
                      fontWeight: isSelected ? '600' : '500',
                      userSelect: 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.target.style.backgroundColor = '#f8fafc';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.target.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        const newValue = isSelected 
                          ? currentValue.filter(v => v !== option)
                          : [...(Array.isArray(currentValue) ? currentValue : []), option];
                        updateTaskAdditionalField(group.id, task.id, field.name, newValue);
                      }}
                      style={{
                        width: '14px',
                        height: '14px',
                        margin: '0',
                        cursor: 'pointer',
                        accentColor: '#3b82f6'
                      }}
                    />
                    <span style={{ 
                      flex: 1,
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }} title={option}>
                      {option}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      );

    default:
      return (
        <input
          type="text"
          value={currentValue}
          onChange={(e) => updateTaskAdditionalField(group.id, task.id, field.name, e.target.value)}
          style={{
            width: '100%',
            maxWidth: '120px',
            padding: '4px 8px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '12px',
            backgroundColor: '#f9fafb'
          }}
          placeholder={`${field.name}...`}
        />
      );
  }
};

const renderFieldEditInput = (field, value, onChange) => {
  switch (field.type) {
    case 'text':
      return (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px'
          }}
          placeholder={`Enter ${field.name}`}
        />
      );

    case 'date':
      return (
        <input
          type="date"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px'
          }}
        />
      );

    case 'single_choice':
      return (
        <div style={{ position: 'relative' }}>
          <select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 40px 12px 16px',
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              fontSize: '14px',
              backgroundColor: '#ffffff',
              cursor: 'pointer',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%234b5563' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 16px center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '18px',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)',
              fontWeight: '500',
              color: value ? '#1f2937' : '#9ca3af'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.08), 0 1px 3px 0 rgba(0, 0, 0, 0.08)';
              e.target.style.outline = 'none';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.03)';
            }}
            onMouseEnter={(e) => {
              if (e.target !== document.activeElement) {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.08)';
              }
            }}
            onMouseLeave={(e) => {
              if (e.target !== document.activeElement) {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.03)';
              }
            }}
          >
            <option value="" style={{ color: '#9ca3af', fontWeight: '400' }}>Select an option...</option>
            {field.options?.map(option => (
              <option key={option} value={option} style={{ 
                color: '#111827', 
                fontWeight: '500',
                padding: '12px 16px'
              }}>
                {option}
              </option>
            ))}
          </select>
        </div>
      );

    case 'multiple_choice':
      return (
        <div style={{ position: 'relative' }}>
          {/* Main Trigger */}
          <button
            type="button"
            aria-haspopup="listbox"
            aria-expanded={false}
            style={{
              width: '100%',
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
              backgroundColor: '#ffffff',
              minHeight: '52px',
              padding: '12px 40px 12px 16px',
              cursor: 'pointer',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
              transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              textAlign: 'left',
              outline: 'none'
            }}
            onClick={(e) => {
              const dropdown = e.currentTarget.nextElementSibling;
              const isVisible = dropdown.style.display === 'block';
              const isExpanded = !isVisible;
              
              // Close all other dropdowns
              document.querySelectorAll('.edit-multiselect-dropdown').forEach(d => {
                d.style.display = 'none';
              });
              
              dropdown.style.display = isExpanded ? 'block' : 'none';
              e.currentTarget.setAttribute('aria-expanded', isExpanded);
              
              // Update styling
              if (isExpanded) {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.08), 0 1px 3px 0 rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.borderBottomLeftRadius = '10px';
                e.currentTarget.style.borderBottomRightRadius = '10px';
              } else {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.05)';
              }
            }}
            onMouseEnter={(e) => {
              if (e.currentTarget.getAttribute('aria-expanded') !== 'true') {
                e.currentTarget.style.borderColor = '#cbd5e0';
                e.currentTarget.style.boxShadow = '0 1px 4px 0 rgba(0, 0, 0, 0.08)';
              }
            }}
            onMouseLeave={(e) => {
              if (e.currentTarget.getAttribute('aria-expanded') !== 'true') {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.05)';
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.currentTarget.click();
              } else if (e.key === 'Escape') {
                const dropdown = e.currentTarget.nextElementSibling;
                dropdown.style.display = 'none';
                e.currentTarget.setAttribute('aria-expanded', 'false');
              }
            }}
          >
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px',
              alignItems: 'center',
              overflow: 'hidden',
              flex: 1,
              minHeight: '24px'
            }}>
              {Array.isArray(value) && value.length > 0 ? (
                value.length <= 3 ? (
                  // Show individual tags for 1-3 selections
                  value.map((selectedValue, index) => (
                    <span
                      key={selectedValue}
                      style={{
                        backgroundColor: '#eff6ff',
                        color: '#2563eb',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        border: '1px solid #bfdbfe',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        maxWidth: index === 0 ? '120px' : '80px',
                        overflow: 'hidden'
                      }}
                      title={selectedValue}
                    >
                      <span style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {selectedValue}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const newValue = value.filter(v => v !== selectedValue);
                          onChange(newValue);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#2563eb',
                          cursor: 'pointer',
                          padding: '0',
                          fontSize: '14px',
                          lineHeight: '1',
                          width: '16px',
                          height: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          transition: 'all 0.15s ease',
                          flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#dbeafe';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))
                ) : (
                  // Show count and first item for 4+ selections
                  <>
                    <span style={{
                      backgroundColor: '#f0fdf4',
                      color: '#166534',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '700',
                      border: '1px solid #bbf7d0'
                    }}>
                      {value.length} items
                    </span>
                    <span style={{
                      backgroundColor: '#eff6ff',
                      color: '#2563eb',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '600',
                      border: '1px solid #bfdbfe',
                      maxWidth: '100px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={value[0]}
                    >
                      {value[0]}
                    </span>
                    {value.length > 1 && (
                      <span style={{
                        color: '#6b7280',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        +{value.length - 1} more
                      </span>
                    )}
                  </>
                )
              ) : (
                <span style={{ color: '#9ca3af', fontSize: '14px', fontWeight: '400' }}>
                  Select options...
                </span>
              )}
            </div>
            
            {/* Chevron Icon */}
            <svg
              style={{
                position: 'absolute',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                color: '#6b7280',
                transition: 'transform 0.15s ease'
              }}
              width="16" 
              height="16" 
              viewBox="0 0 20 20" 
              fill="none"
            >
              <path 
                stroke="currentColor" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth="1.5" 
                d="M6 8l4 4 4-4"
              />
            </svg>
          </button>
          
          {/* Dropdown Menu */}
          <div
            className="edit-multiselect-dropdown"
            role="listbox"
            aria-multiselectable="true"
            style={{
              display: 'none',
              position: 'absolute',
              top: '100%',
              left: '0',
              right: '0',
              backgroundColor: '#ffffff',
              border: '1px solid #d1d5db',
              borderTop: 'none',
              borderRadius: '0 0 12px 12px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              zIndex: 1000,
              maxHeight: '280px',
              overflowY: 'auto',
              marginTop: '-1px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search Box */}
            {field.options && field.options.length > 5 && (
              <div style={{
                padding: '12px',
                borderBottom: '1px solid #f1f5f9',
                backgroundColor: '#f8fafc'
              }}>
                <input
                  type="text"
                  placeholder="Search options..."
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '13px',
                    backgroundColor: '#ffffff',
                    outline: 'none',
                    transition: 'border-color 0.15s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                  }}
                  onChange={(e) => {
                    const searchTerm = e.target.value.toLowerCase();
                    const options = e.target.closest('.edit-multiselect-dropdown').querySelectorAll('.option-item');
                    options.forEach(option => {
                      const text = option.textContent.toLowerCase();
                      option.style.display = text.includes(searchTerm) ? 'flex' : 'none';
                    });
                  }}
                />
              </div>
            )}

            {/* Header with Select All/Clear All */}
            <div style={{
              padding: '12px',
              borderBottom: '1px solid #f1f5f9',
              backgroundColor: '#f8fafc',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange([...field.options]);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#3b82f6',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    transition: 'background-color 0.15s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#eff6ff'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 4L6 11l-3-3"/>
                  </svg>
                  Select All
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange([]);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#ef4444',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    transition: 'background-color 0.15s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#fef2f2'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 4L4 12m8 0L4 4"/>
                  </svg>
                  Clear All
                </button>
              </div>
              <span style={{
                fontSize: '11px',
                color: '#6b7280',
                fontWeight: '500'
              }}>
                {Array.isArray(value) ? value.length : 0} / {field.options?.length || 0}
              </span>
            </div>

            {/* Options List */}
            <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
              {field.options?.map((option, index) => {
                const isSelected = Array.isArray(value) && value.includes(option);
                return (
                  <label
                    key={option}
                    className="option-item"
                    role="option"
                    aria-selected={isSelected}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      fontSize: '13px',
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#f0f9ff' : 'transparent',
                      color: isSelected ? '#1e40af' : '#374151',
                      transition: 'all 0.1s cubic-bezier(0.4, 0, 0.2, 1)',
                      borderBottom: index < field.options.length - 1 ? '1px solid #f8fafc' : 'none',
                      fontWeight: isSelected ? '600' : '500',
                      userSelect: 'none',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.target.style.backgroundColor = '#f8fafc';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.target.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {/* Custom Checkbox */}
                    <div style={{
                      width: '18px',
                      height: '18px',
                      border: isSelected ? '2px solid #3b82f6' : '2px solid #d1d5db',
                      borderRadius: '4px',
                      backgroundColor: isSelected ? '#3b82f6' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                      flexShrink: 0,
                      position: 'relative'
                    }}>
                      {isSelected && (
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                          <path 
                            stroke="white" 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth="2" 
                            d="M13 4L6 11l-3-3"
                          />
                        </svg>
                      )}
                    </div>
                    
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        const newValue = isSelected 
                          ? value.filter(v => v !== option)
                          : [...(Array.isArray(value) ? value : []), option];
                        onChange(newValue);
                      }}
                      style={{
                        position: 'absolute',
                        opacity: 0,
                        cursor: 'pointer',
                        width: '18px',
                        height: '18px',
                        left: '16px'
                      }}
                    />
                    
                    <span style={{ 
                      flex: 1,
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      paddingRight: '8px'
                    }} title={option}>
                      {option}
                    </span>
                    
                    {/* Selection indicator */}
                    {isSelected && (
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: '#3b82f6',
                        flexShrink: 0
                      }} />
                    )}
                  </label>
                );
              })}
            </div>
            
            {/* Footer with selection summary */}
            {Array.isArray(value) && value.length > 0 && (
              <div style={{
                padding: '12px 16px',
                borderTop: '1px solid #f1f5f9',
                backgroundColor: '#f8fafc',
                fontSize: '11px',
                color: '#6b7280',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>
                  {value.length} option{value.length !== 1 ? 's' : ''} selected
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const dropdown = e.target.closest('.edit-multiselect-dropdown');
                    const trigger = dropdown.previousElementSibling;
                    dropdown.style.display = 'none';
                    trigger.setAttribute('aria-expanded', 'false');
                    trigger.style.borderColor = '#e2e8f0';
                    trigger.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.05)';
                  }}
                  style={{
                    background: 'none',
                    border: '1px solid #e2e8f0',
                    color: '#374151',
                    fontSize: '11px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8fafc';
                    e.target.style.borderColor = '#d1d5db';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = '#e2e8f0';
                  }}
                >
                  Done
                </button>
              </div>
            )}
          </div>
          
          {/* Helper Text */}
          <div style={{ 
            fontSize: '12px', 
            color: '#6b7280', 
            marginTop: '8px',
            fontWeight: '400'
          }}>
            Click to select multiple options
          </div>
        </div>
      );

    default:
      return (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px'
          }}
          placeholder={`Enter ${field.name}`}
        />
      );
  }
};

// Utility function to close all dropdowns when clicking outside
document.addEventListener('click', (e) => {
  if (!e.target.closest('.compact-multiselect-dropdown') && 
      !e.target.closest('button[aria-haspopup="listbox"]')) {
    document.querySelectorAll('.compact-multiselect-dropdown, .edit-multiselect-dropdown').forEach(dropdown => {
      dropdown.style.display = 'none';
      const trigger = dropdown.previousElementSibling;
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.style.borderColor = '#e2e8f0';
        trigger.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.05)';
      }
    });
  }
});

  const getStatusColor = (status) => {
    const colors = {
      'pending': { backgroundColor: '#fef3c7', color: '#92400e' },
      'in-progress': { backgroundColor: '#dbeafe', color: '#1e40af' },
      'done': { backgroundColor: '#d1fae5', color: '#065f46' },
      'overdue': { backgroundColor: '#fecaca', color: '#991b1b' }
    };
    return colors[status] || colors.pending;
  };

  const getTypeIcon = (type) => {
    const icons = {
      'Task': '📋',
      'Milestone': '🏁',
      'Review': '👀',
      'Approval': '✅'
    };
    return icons[type] || '📋';
  };

  // Styles
  const successplancontainerStyles = {
    minHeight: '100vh',
    backgroundColor: '#f1f5f9',
    padding: '24px'
  };

  const successplanStyles = {
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '32px',
      padding: '24px 32px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderRadius: '16px',
      color: 'white',
      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
    },
    headerTitle: {
      fontSize: '28px',
      fontWeight: '700',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    },
    headerActions: {
      display: 'flex',
      gap: '16px',
      alignItems: 'center'
    },
    actionsDropdownContainer: {
      position: 'relative'
    }
  };

  const successplanactionsButtonStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  };

  const successplandropdownMenuStyles = {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '8px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    border: '1px solid #e2e8f0',
    minWidth: '200px',
    zIndex: 1000
  };

  const successplandropdownItemStyles = {
    display: 'block',
    width: '100%',
    padding: '12px 16px',
    color: '#374151',
    fontSize: '14px',
    border: 'none',
    background: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    borderBottom: '1px solid #f1f5f9',
    transition: 'background-color 0.2s ease'
  };

  const [availableSteps,setavailableSteps] = useState([])

  return (
    <div style={successplancontainerStyles}>
      <div style={successplanStyles.header}>
        <h1 style={successplanStyles.headerTitle}>
          <TrendingUp size={32} />
          Success Plan
        </h1>

        <div style={successplanStyles.headerActions}>
          <div style={successplanStyles.actionsDropdownContainer}>
            <button 
              onClick={() => setShowActionsDropdown(!showActionsDropdown)}
              style={successplanactionsButtonStyles}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
            >
              Actions
              <ChevronDown size={16} />
            </button>

            {showActionsDropdown && (
              <div 
                style={successplandropdownMenuStyles}
                onMouseLeave={() => setShowActionsDropdown(false)}
              >
                <button 
                  style={successplandropdownItemStyles}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  onClick={() => {
                    setshowsavetemplate(true)
                    setShowActionsDropdown(false)
                  }}
                >
                  Save as Template
                </button>
                <button 
                  style={{...successplandropdownItemStyles, borderBottom: 'none'}}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  onClick={() => setShowActionsDropdown(false)}
                >
                  Load from Template
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
      }}>
        {taskGroups.map((group, groupIndex) => (
          <div key={group.id} style={{
            borderBottom: groupIndex === taskGroups.length - 1 ? 'none' : '2px solid #f1f5f9'
          }}>
            {/* Group Header */}
            <div style={{
              backgroundColor: '#f9fafb',
              padding: '16px 24px',
              borderBottom: expandedGroups.has(group.id) ? '1px solid #e5e7eb' : 'none'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <button
                    onClick={() => toggleGroup(group.id)}
                    style={{
                      color: '#6b7280',
                      cursor: 'pointer',
                      border: 'none',
                      background: 'none'
                    }}
                    onMouseOver={(e) => e.target.style.color = '#374151'}
                    onMouseOut={(e) => e.target.style.color = '#6b7280'}
                  >
                    {expandedGroups.has(group.id) ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  </button>
                  
                  {editingGroup === group.id ? (
                    <input
                      type="text"
                      value={group.title}
                      onChange={(e) => {
                        setTaskGroups(prev => prev.map(g =>
                          g.id === group.id ? { ...g, title: e.target.value } : g
                        ));
                      }}
                      onBlur={() => setEditingGroup(null)}
                      onKeyPress={(e) => e.key === 'Enter' && setEditingGroup(null)}
                      style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#111827',
                        backgroundColor: 'transparent',
                        borderBottom: '1px solid #2563eb',
                        border: 'none',
                        outline: 'none'
                      }}
                      autoFocus
                    />
                  ) : (
                    <span
                      style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#111827',
                        cursor: 'pointer'
                      }}
                      onMouseOver={(e) => e.target.style.color = '#2563eb'}
                      onMouseOut={(e) => e.target.style.color = '#111827'}
                      onClick={() => setEditingGroup(group.id)}
                    >
                      {group.title}
                    </span>
                  )}
                  
                  <span style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    backgroundColor: '#e5e7eb',
                    padding: '2px 8px',
                    borderRadius: '12px'
                  }}>
                    {group.tasks.length} tasks
                  </span>
                  
                  {group.additional_fields && group.additional_fields.length > 0 && (
                    <span style={{
                      fontSize: '12px',
                      color: '#2563eb',
                      backgroundColor: '#eff6ff',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      border: '1px solid #bfdbfe'
                    }}>
                      {group.additional_fields.length} custom fields
                    </span>
                  )}
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <button
                    onClick={() => setManagingFieldsForGroup(group.id)}
                    style={{
                      padding: '6px',
                      color: '#2563eb',
                      cursor: 'pointer',
                      border: 'none',
                      background: 'none',
                      borderRadius: '4px'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.color = '#1d4ed8';
                      e.target.style.backgroundColor = '#eff6ff';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.color = '#2563eb';
                      e.target.style.backgroundColor = 'transparent';
                    }}
                    title="Manage fields"
                  >
                    <Settings size={16} />
                  </button>
                  
                  <button
                    onClick={() => moveGroup(group.id, 'up')}
                    disabled={groupIndex === 0}
                    style={{
                      padding: '6px',
                      color: groupIndex === 0 ? '#d1d5db' : '#9ca3af',
                      cursor: groupIndex === 0 ? 'not-allowed' : 'pointer',
                      border: 'none',
                      background: 'none',
                      borderRadius: '4px'
                    }}
                    onMouseOver={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#f3f4f6')}
                    onMouseOut={(e) => !e.target.disabled && (e.target.style.backgroundColor = 'transparent')}
                    title="Move up"
                  >
                    <ChevronUp size={16} />
                  </button>
                  
                  <button
                    onClick={() => moveGroup(group.id, 'down')}
                    disabled={groupIndex === taskGroups.length - 1}
                    style={{
                      padding: '6px',
                      color: groupIndex === taskGroups.length - 1 ? '#d1d5db' : '#9ca3af',
                      cursor: groupIndex === taskGroups.length - 1 ? 'not-allowed' : 'pointer',
                      border: 'none',
                      background: 'none',
                      borderRadius: '4px'
                    }}
                    onMouseOver={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#f3f4f6')}
                    onMouseOut={(e) => !e.target.disabled && (e.target.style.backgroundColor = 'transparent')}
                    title="Move down"
                  >
                    <ChevronDown size={16} />
                  </button>
                  
                  <button
                    onClick={() => addStep(group.id)}
                    style={{
                      padding: '6px',
                      color: '#2563eb',
                      cursor: 'pointer',
                      border: 'none',
                      background: 'none',
                      borderRadius: '4px'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.color = '#1d4ed8';
                      e.target.style.backgroundColor = '#eff6ff';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.color = '#2563eb';
                      e.target.style.backgroundColor = 'transparent';
                    }}
                    title="Add task"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Expanded Group Content */}
            {expandedGroups.has(group.id) && (
              <div>
                {/* Tasks Table for this group */}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%' }}>
                    <thead style={{
                      backgroundColor: '#f3f4f6',
                      borderBottom: '1px solid #d1d5db'
                    }}>
                      <tr>
                        <th style={{
                          textAlign: 'left',
                          padding: '12px 16px',
                          fontWeight: '500',
                          color: '#374151',
                          fontSize: '13px'
                        }}>Task</th>
                        <th style={{
                          textAlign: 'left',
                          padding: '12px 16px',
                          fontWeight: '500',
                          color: '#374151',
                          fontSize: '13px'
                        }}>Owner</th>
                        <th style={{
                          textAlign: 'left',
                          padding: '12px 16px',
                          fontWeight: '500',
                          color: '#374151',
                          fontSize: '13px'
                        }}>Wait Days</th>
                        <th style={{
                          textAlign: 'left',
                          padding: '12px 16px',
                          fontWeight: '500',
                          color: '#374151',
                          fontSize: '13px'
                        }}>Type</th>
                        <th style={{
                          textAlign: 'left',
                          padding: '12px 16px',
                          fontWeight: '500',
                          color: '#374151',
                          fontSize: '13px'
                        }}>Portal</th>
                        <th style={{
                          textAlign: 'left',
                          padding: '12px 16px',
                          fontWeight: '500',
                          color: '#374151',
                          fontSize: '13px'
                        }}>Email Template</th>
                        <th style={{
                          textAlign: 'left',
                          padding: '12px 16px',
                          fontWeight: '500',
                          color: '#374151',
                          fontSize: '13px'
                        }}>Status</th>
                        {/* Group-specific additional fields */}
                        {group.additional_fields?.map(field => (
                          <th key={field.name} style={{
                            textAlign: 'left',
                            padding: '12px 16px',
                            fontWeight: '500',
                            color: '#374151',
                            fontSize: '13px'
                          }}>
                            {field.name}
                            <span style={{
                              fontSize: '10px',
                              color: '#9ca3af',
                              marginLeft: '4px',
                              fontWeight: '400'
                            }}>
                              ({field.type.replace('_', ' ')})
                            </span>
                          </th>
                        ))}
                        <th style={{
                          textAlign: 'left',
                          padding: '12px 16px',
                          fontWeight: '500',
                          color: '#374151',
                          fontSize: '13px'
                        }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.tasks.map((task) => {
                        const isLocked = isTaskLocked(task, group.id);
                        const isDocExpanded = expandedDocs.has(task.id);
                        
                        return (
                          <React.Fragment key={task.id}>
                            <tr style={{
                              borderBottom: '1px solid #f1f5f9',
                              backgroundColor: 'white',
                              opacity: isLocked ? 0.6 : 1
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#fafbfc'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}>
                              
                              {/* Task Column */}
                              <td style={{ padding: '12px 16px' }}>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: '8px'
                                }}>
                                  {isLocked && (
                                    <div style={{ 
                                      color: '#9ca3af',
                                      marginTop: '2px'
                                    }} title="Locked - dependencies not completed">
                                      🔒
                                    </div>
                                  )}
                                  <div style={{ flex: 1 }}>
                                    <div style={{
                                      fontWeight: '500',
                                      color: '#111827',
                                      fontSize: '14px'
                                    }}>{task.title}</div>
                                    
                                    {task.description && (
                                      <div style={{
                                        fontSize: '12px',
                                        color: '#6b7280',
                                        marginTop: '2px'
                                      }}>{task.description}</div>
                                    )}
                                    
                                    {task.dependencies.length > 0 && (
                                      <div style={{
                                        fontSize: '11px',
                                        color: '#6b7280',
                                        marginTop: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}>
                                        <Link size={10} />
                                        Depends on: {task.dependencies.length} task(s)
                                      </div>
                                    )}
                                    
                                    {task.documentation && (
                                      <div style={{ marginTop: '6px' }}>
                                        <button 
                                          style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            fontSize: '11px',
                                            color: '#2563eb',
                                            fontWeight: '500',
                                            cursor: 'pointer',
                                            border: 'none',
                                            background: 'none',
                                            gap: '4px',
                                            padding: '2px 0'
                                          }}
                                          onMouseOver={(e) => e.target.style.color = '#1d4ed8'}
                                          onMouseOut={(e) => e.target.style.color = '#2563eb'}
                                          onClick={() => toggleDocumentation(task.id)}
                                        >
                                          <FileText size={10} />
                                          {isDocExpanded ? 'Hide Docs' : 'View Docs'}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>

                              {/* Owner Column */}
                              <td style={{ padding: '12px 16px' }}>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}>
                                  <div style={{
                                    width: '28px',
                                    height: '28px',
                                    backgroundColor: '#ec4899',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '12px',
                                    fontWeight: '500'
                                  }}>
                                    {(allusersnames.find(user => user.id === task.assignee)?.name || 'U').charAt(0).toUpperCase()}
                                  </div>
                                  <span style={{
                                    fontSize: '13px',
                                    color: '#374151'
                                  }}>{allusersnames.find(user => user.id === task.assignee)?.name || 'Unassigned'}</span>
                                </div>
                              </td>

                              {/* Wait Days Column */}
                              <td style={{
                                padding: '12px 16px',
                                textAlign: 'center'
                              }}>
                                <span style={{
                                  fontSize: '13px',
                                  color: '#111827',
                                  fontWeight: '500'
                                }}>{task.waitDays}</span>
                              </td>

                              {/* Type Column */}
                              <td style={{ padding: '12px 16px' }}>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}>
                                  <span style={{ fontSize: '13px' }}>{getTypeIcon(task.type)}</span>
                                  <span style={{
                                    fontSize: '13px',
                                    color: '#374151'
                                  }}>{task.type}</span>
                                </div>
                              </td>

                              {/* Portal Column */}
                              <td style={{ padding: '12px 16px' }}>
                                <button
                                  onClick={() => updateStep(group.id, task.id, { sharedInPortal: !task.sharedInPortal })}
                                  style={{
                                    width: '42px',
                                    height: '20px',
                                    borderRadius: '10px',
                                    backgroundColor: task.sharedInPortal ? '#f97316' : '#d1d5db',
                                    border: 'none',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    transition: 'background-color 0.2s'
                                  }}
                                >
                                  <div style={{
                                    width: '16px',
                                    height: '16px',
                                    backgroundColor: 'white',
                                    borderRadius: '50%',
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                    position: 'absolute',
                                    top: '2px',
                                    left: task.sharedInPortal ? '24px' : '2px',
                                    transition: 'left 0.2s'
                                  }} />
                                </button>
                              </td>

                              {/* Email Template Column */}
                              <td style={{ padding: '12px 16px' }}>
                                <span style={{
                                  fontSize: '13px',
                                  color: '#374151'
                                }}>{task.emailTemplate}</span>
                              </td>

                              {/* Status Column */}
                              <td style={{ padding: '12px 16px' }}>
                                <select
                                  value={task.status}
                                  onChange={(e) => updateStep(group.id, task.id, { status: e.target.value })}
                                  disabled={isLocked}
                                  style={{
                                    padding: '3px 10px',paddingRight: '28px',
                                    borderRadius: '9999px',
                                    fontSize: '11px',
                                    fontWeight: '500',
                                    cursor: isLocked ? 'not-allowed' : 'pointer',
                                    border: 'none',
                                    ...getStatusColor(task.status)
                                  }}
                                >
                                  {statusOptions.map(status => (
                                    <option key={status} value={status}>
                                      {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              
                              {/* Dynamic columns for additional fields */}
                              {group.additional_fields?.map(field => (
                                <td key={field.name} style={{ 
                                  padding: '12px 16px'
                                }}>
                                  {renderFieldInput(group, task, field)}
                                </td>
                              ))}
                              
                              {/* Actions Column */}
                              <td style={{ padding: '12px 16px' }}>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}>
                                  <button
                                    onClick={() => {
                                      setavailableSteps(taskGroups.flatMap(group =>
                                        group.tasks.filter(s => s.id !== task.id).map(s => ({ ...s, groupId: group.id }))
                                      ))
                                      setEditingStep({ step: task, groupId: group.id })
                                    }}
                                    style={{
                                      padding: '4px',
                                      color: '#9ca3af',
                                      cursor: 'pointer',
                                      border: 'none',
                                      background: 'none',
                                      borderRadius: '3px'
                                    }}
                                    onMouseOver={(e) => {
                                      e.target.style.color = '#4b5563';
                                      e.target.style.backgroundColor = '#f3f4f6';
                                    }}
                                    onMouseOut={(e) => {
                                      e.target.style.color = '#9ca3af';
                                      e.target.style.backgroundColor = 'transparent';
                                    }}
                                    title="Edit task"
                                  >
                                    <Edit3 size={14} />
                                  </button>
                                  <button
                                    onClick={() => deleteStep(group.id, task.id)}
                                    style={{
                                      padding: '4px',
                                      color: '#9ca3af',
                                      cursor: 'pointer',
                                      border: 'none',
                                      background: 'none',
                                      borderRadius: '3px'
                                    }}
                                    onMouseOver={(e) => {
                                      e.target.style.color = '#dc2626';
                                      e.target.style.backgroundColor = '#fef2f2';
                                    }}
                                    onMouseOut={(e) => {
                                      e.target.style.color = '#9ca3af';
                                      e.target.style.backgroundColor = 'transparent';
                                    }}
                                    title="Delete task"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                            
                            {/* Documentation Panel Row */}
                            {isDocExpanded && task.documentation && (
                              <tr>
                                <td colSpan={8 + (group.additional_fields?.length || 0)} style={{ 
                                  padding: '0 16px 12px 16px'
                                }}>
                                  <div style={{
                                    background: 'linear-gradient(to right, #eff6ff, #eef2ff)',
                                    border: '1px solid #bfdbfe',
                                    borderRadius: '8px',
                                    padding: '12px',
                                    marginLeft: '32px',
                                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                                  }}>
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      justifyContent: 'space-between',
                                      marginBottom: '8px'
                                    }}>
                                      <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                      }}>
                                        <div style={{
                                          width: '6px',
                                          height: '6px',
                                          backgroundColor: '#3b82f6',
                                          borderRadius: '50%'
                                        }}></div>
                                        <h4 style={{
                                          fontSize: '13px',
                                          fontWeight: '600',
                                          color: '#1e3a8a',
                                          margin: 0
                                        }}>Documentation</h4>
                                      </div>
                                      <button 
                                        style={{
                                          color: '#60a5fa',
                                          cursor: 'pointer',
                                          border: 'none',
                                          background: 'none',
                                          padding: '2px'
                                        }}
                                        onMouseOver={(e) => e.target.style.color = '#2563eb'}
                                        onMouseOut={(e) => e.target.style.color = '#60a5fa'}
                                        onClick={() => toggleDocumentation(task.id)}
                                      >
                                        <X size={14} />
                                      </button>
                                    </div>
                                    <div style={{
                                      fontSize: '13px',
                                      color: '#1e40af',
                                      lineHeight: '1.4'
                                    }}>
                                      {task.documentation}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Add Task Button */}
                <div style={{
                  padding: '12px 24px',
                  borderTop: '1px solid #f1f5f9',
                  backgroundColor: '#fafbfc'
                }}>
                  <button
                    onClick={() => addStep(group.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#2563eb',
                      fontSize: '13px',
                      cursor: 'pointer',
                      border: 'none',
                      background: 'none',
                      padding: '6px 0'
                    }}
                    onMouseOver={(e) => e.target.style.color = '#1d4ed8'}
                    onMouseOut={(e) => e.target.style.color = '#2563eb'}
                  >
                    <Plus size={14} />
                    <span>Add task to {group.title}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Field Management Modal */}
      {managingFieldsForGroup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '600px',
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>
              Manage Additional Fields
            </h3>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Current Fields:
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {taskGroups.find(g => g.id === managingFieldsForGroup)?.additional_fields?.map((field, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>{field.name}</span>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        Type: {field.type.replace('_', ' ').charAt(0).toUpperCase() + field.type.replace('_', ' ').slice(1)}
                        {field.options && field.options.length > 0 && (
                          <span> • Options: {field.options.join(', ')}</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => removeFieldFromGroup(managingFieldsForGroup, field.name)}
                      style={{
                        color: '#dc2626',
                        cursor: 'pointer',
                        border: 'none',
                        background: 'none',
                        padding: '4px',
                        borderRadius: '4px'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.color = '#b91c1c';
                        e.target.style.backgroundColor = '#fef2f2';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.color = '#dc2626';
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                )) || []}
              </div>
            </div>

            <div style={{ marginBottom: '20px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>
                Add New Field
              </h4>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
                    Field Name:
                  </label>
                  <input
                    type="text"
                    value={newFieldName}
                    onChange={(e) => setNewFieldName(e.target.value)}
                    placeholder="Enter field name"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
                    Field Type:
                  </label>
                  <select
                    value={newFieldType}
                    onChange={(e) => setNewFieldType(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    {fieldTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                {(newFieldType === 'single_choice' || newFieldType === 'multiple_choice') && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
                      Options (comma-separated):
                    </label>
                    <input
                      type="text"
                      value={newFieldOptions}
                      onChange={(e) => setNewFieldOptions(e.target.value)}
                      placeholder="Option 1, Option 2, Option 3"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Enter options separated by commas
                    </div>
                  </div>
                )}

                <button
                  onClick={() => addFieldToGroup(managingFieldsForGroup, newFieldName, newFieldType, newFieldOptions)}
                  disabled={!newFieldName.trim() || ((newFieldType === 'single_choice' || newFieldType === 'multiple_choice') && !newFieldOptions.trim())}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: (!newFieldName.trim() || ((newFieldType === 'single_choice' || newFieldType === 'multiple_choice') && !newFieldOptions.trim())) ? '#d1d5db' : '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: (!newFieldName.trim() || ((newFieldType === 'single_choice' || newFieldType === 'multiple_choice') && !newFieldOptions.trim())) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Add Field
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setManagingFieldsForGroup(null);
                  setNewFieldName('');
                  setNewFieldType('text');
                  setNewFieldOptions('');
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Edit Modal */}
      {editingStep && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '800px',
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>
              Edit Task
            </h3>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Task Name
              </label>
              <input
                type="text"
                value={editingStep.step.title}
                onChange={(e) => setEditingStep(prev => ({
                  ...prev,
                  step: { ...prev.step, title: e.target.value }
                }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Description
              </label>
              <textarea
                value={editingStep.step.description || ''}
                onChange={(e) => setEditingStep(prev => ({
                  ...prev,
                  step: { ...prev.step, description: e.target.value }
                }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  minHeight: '80px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Documentation
              </label>
              <textarea
                value={editingStep.step.documentation || ''}
                onChange={(e) => setEditingStep(prev => ({
                  ...prev,
                  step: { ...prev.step, documentation: e.target.value }
                }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  minHeight: '60px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '4px'
                }}>Status</label>
                <select
                  value={editingStep.step.status}
                  onChange={(e) => setEditingStep(prev => ({ ...prev, status: e.target.value }))}
                  style={{
                    width: '100%',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    padding: '8px 12px'
                  }}
                >
                  {statusOptions.map(status => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
                    </option>
                  ))}
                </select>
              </div>
  
              <div>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <input
                    type="checkbox"
                    checked={editingStep.task.sharedInPortal}
                    onChange={(e) => setEditingStep(prev => ({ ...prev, sharedInPortal: e.target.checked }))}
                    style={{ borderRadius: '4px' }}
                  />
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>Shared in Portal</span>
                </label>
              </div>
  
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '4px'
                }}>Dependencies</label>
                <div style={{
                  maxHeight: '128px',
                  overflowY: 'auto',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  padding: '8px'
                }}>
                  {availableSteps.map(availableStep => (
                    <label 
                      key={availableStep.id} 
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '4px 0'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={editingStep.dependencies.includes(availableStep.id)}
                        
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditingStep(prev => ({
                              ...prev,
                              dependencies: [...prev.dependencies, availableStep.id]
                            }));
                            
                          } else {
                            setEditingStep(prev => ({
                              ...prev,
                              dependencies: prev.dependencies.filter(id => id !== availableStep.id)
                            }));
                          }
                        }}
                        style={{ borderRadius: '4px' }}
                      />
                      <span style={{ fontSize: '14px' }}>{availableStep.name}</span>
                    </label>
                  ))}
                </div>
              </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Type
                </label>
                <select
                  value={editingStep.type}
                  onChange={(e) => setEditingStep(prev => ({
                    ...prev,
                    task: { ...prev.task, type: e.target.value }
                  }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  {typeOptions.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Wait Days
                </label>
                <input
                  type="number"
                  min="0"
                  value={editingStep.step.waitDays}
                  onChange={(e) => setEditingStep(prev => ({
                    ...prev,
                    step: { ...prev.step, waitDays: parseInt(e.target.value) || 0 }
                  }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Email Template
              </label>
              <input
                type="text"
                value={editingStep.step.emailTemplate}
                onChange={(e) => setEditingStep(prev => ({
                  ...prev,
                  step: { ...prev.step, emailTemplate: e.target.value }
                }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Additional Fields for the current group */}
            {taskGroups.find(g => g.id === editingStep.groupId)?.additional_fields?.length > 0 && (
              <div style={{
                marginBottom: '16px',
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                  Additional Fields
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
                  {taskGroups.find(g => g.id === editingStep.groupId)?.additional_fields?.map(field => (
                    <div key={field.name}>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '4px', 
                        fontWeight: '500', 
                        fontSize: '14px',
                        color: '#374151'
                      }}>
                        {field.name}
                        <span style={{
                          fontSize: '12px',
                          color: '#6b7280',
                          fontWeight: '400',
                          marginLeft: '4px'
                        }}>
                          ({field.type.replace('_', ' ')})
                        </span>
                      </label>
                      {renderFieldEditInput(field, editingStep.step.additional_fields?.[field.name], (value) => 
                        setEditingStep(prev => ({
                          ...prev,
                          step: {
                            ...prev.step,
                            additional_fields: {
                              ...prev.step.additional_fields,
                              [field.name]: value
                            }
                          }
                        }))
                      )}
                    </div>
                  )) || []}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setEditingStep(null)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  updateStep(editingStep.groupId, editingStep.step.id, editingStep.step);
                  setEditingStep(null);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuccessPlanTable;